import { useProductsStore } from '../stores/products/products.composable';
import { computed } from 'vue';
import { infuse } from '../ioc-config';
import { useI18n } from './i18n.composable';
import Vehicle from '../models/vehicle.model';

export const useDealerInfo = (product: Vehicle) => {
  const productsStore = useProductsStore();
  const { t } = useI18n();
  const { dealerLocator } = infuse('config');

  // Get loading state for a product
  const loading = computed(() =>
    productsStore.state.loadingProducts.some((loadingProduct) => loadingProduct === product.code),
  );
  // Get a vendor for current product
  const vendor = computed(() => productsStore.state.vendors.find((vendor) => vendor.item.productCode === product.code));
  // Get all the point of services (POS) for it
  const pointOfServices = computed(() => vendor.value?.item.pointOfServices.map((pos) => pos.name) || []);
  // Get detailed dealers information for current POS
  const dealersInfo = computed(() =>
    productsStore.state.dealersInfo.filter((info) => pointOfServices.value.includes(info.item.baseInfo.externalId)),
  );

  const dealerName = computed(() => {
    // Return nothing if it's still loading
    if (loading.value) return;

    let name: string | undefined;

    // Just return translated "wholeSale" label in case of vehicle was tagged as "wholeSale"
    if (product.wholeSale) {
      name = t('dcp.products.dealerName.wholeSale');
    } else {
      // In case of active "enhancedName" FE configuration
      if (dealerLocator.enableEnhancedName) {
        // Get all business names of the POS as an array of strings and remove all duplicates
        // Important: we're only interested in MB (Mercedes-Benz) brands
        const businessNames = [
          ...new Set(
            dealersInfo.value
              ?.map((info) => info.item.brands.find((info) => info.brand.code === 'MB')?.businessName)
              .filter(Boolean) || [],
          ),
        ];
        // Get all legal names of the POS as an array of strings and remove all duplicates
        const legalNames = [...new Set(dealersInfo.value?.map((info) => info.item.baseInfo.name1) || [])];

        //  Use legal name as a dealer name if it's only one and same for all POS of the product
        // Use business name as a dealer name if it's only one and same for all POS of the product
        // As a fallback use the "multipleDealers" translated locale

        const { first, second } = dealerLocator.enforceLegalName
          ? { first: legalNames, second: businessNames }
          : { first: businessNames, second: legalNames };

        if (first.length === 1) {
          name = first[0] as string;
        } else if (second.length === 1) {
          name = second[0] as string;
        } else {
          name = t('dcp.dealerName.multipleDealers');
        }
      }
      // In other case return the display name from the product object or set it to the "multipleDealers" translated locale
      else {
        name = product.pointOfServiceDisplayName || t('dcp.dealerName.multipleDealers');
      }
    }

    return name;
  });

  const dealerAddress = computed(() => {
    // Return nothing if it's still loading
    if (loading.value) return;

    let address: string | undefined;

    // In case of active "enhancedName" FE configuration
    if (dealerLocator.enableEnhancedAddress) {
      // Use address from the "dealersInf"o array if only one is provided
      if (dealersInfo.value.length === 1) {
        const addressInfo = dealersInfo.value[0].item.address;

        // Build all necessary properties into one string
        address = dealerLocator.enhancedAddressFieldsOrder
          .map((field) => addressInfo[field])
          .filter((item) => !!item)
          .join(' ');
      }
    }
    // In other case return the "pointOfServiceFormattedAddress" name from the product object
    else {
      address = product.pointOfServiceFormattedAddress;
    }

    return address;
  });

  return {
    dealerName,
    dealerAddress,
    dealersInfo,
  };
};
